/**
 * DeleteConfirmDialog — confirm dialog for deleting a thread (STORY-002-010
 * step 2, AC#4).
 *
 * The project does not ship a shadcn `Dialog` primitive yet (only Button /
 * Input / Label live under `components/ui`, outside this story's allowed-touch
 * list), so this is a self-contained accessible modal built from the design
 * tokens: a focus-trapped `role="dialog"` with `aria-modal`, an overlay, a
 * focused-on-mount cancel button (safe default), Escape to dismiss, and a
 * destructive confirm.
 *
 * Strings from the `chat` i18n namespace; tokens from the Tailwind theme.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

export interface DeleteConfirmDialogProps {
  /** The thread title shown in the body (already redaction-safe / placeholder). */
  threadTitle: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function DeleteConfirmDialog({
  threadTitle,
  onConfirm,
  onCancel,
}: DeleteConfirmDialogProps): ReactElement {
  const { t } = useTranslation("chat");
  const titleId = useId();
  const bodyId = useId();
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  // Focus the safe default (cancel) on mount; Escape cancels.
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const onOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onCancel();
    },
    [onCancel],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onOverlayClick}
      data-testid="chat-delete-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        data-testid="chat-delete-dialog"
        className="w-full max-w-sm rounded-2xl border border-border bg-card p-5 shadow-lg"
      >
        <h2
          id={titleId}
          className="font-display text-[19px]/[1.556] font-bold tracking-tight text-foreground"
        >
          {t("delete.title")}
        </h2>
        <p id={bodyId} className="mt-1.5 text-[15px]/[1.429] text-muted-foreground">
          {t("delete.body")}
        </p>
        <p className="mt-2 truncate text-[15px]/[1.429] font-medium text-foreground">
          {threadTitle}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button
            ref={cancelRef}
            type="button"
            variant="secondary"
            onClick={onCancel}
            data-testid="chat-delete-cancel"
          >
            {t("delete.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            data-testid="chat-delete-confirm"
          >
            {t("delete.confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DeleteConfirmDialog;
